/**
 * Faixa compacta de números no topo do mapa. Os números seguem o filtro e
 * vários são atalho de filtro: tocar em "atrasados" filtra os atrasados;
 * tocar de novo desfaz. O gargalo leva à página do setor.
 *
 * Cor só no ícone/número e só quando o número pede atenção (> 0).
 */
import React from "react";
import {
  FiAlertTriangle,
  FiCheckSquare,
  FiPauseCircle,
  FiPlayCircle,
  FiScissors,
  FiTrendingDown,
  FiUsers,
} from "react-icons/fi";

import { formatarQuantidade, plural } from "../../utils/dinheiro";
import { SITUACAO } from "./setores";
import { Faixa, ItemFaixa, SeparadorFaixa, StatusVivo } from "./styles";

// Filtro que cada número aplica ao ser tocado.
export const ATALHOS_DA_FAIXA = Object.freeze({
  emProducao: { id: "statusAmbiente", valor: ["em_producao"] },
  pausados: { id: "statusAmbiente", valor: ["pausado"] },
  atrasados: { id: "atrasadas", valor: "sim" },
  tarefasAbertas: { id: "tarefas", valor: "com" },
  pecasFora: { id: "pecasFora", valor: "com" },
});

function atalhoLigado(filtros, atalho) {
  const atual = filtros?.[atalho.id];
  if (Array.isArray(atalho.valor)) {
    return Array.isArray(atual) && atual.length === atalho.valor.length && atalho.valor.every((v) => atual.includes(v));
  }
  return atual === atalho.valor;
}

const ITENS = [
  { id: "emProducao", Icone: FiPlayCircle, tom: "info", singular: "em produção", plural: "em produção", dica: "Ambientes em produção na fábrica" },
  { id: "pausados", Icone: FiPauseCircle, tom: "warning", singular: "pausado", plural: "pausados", dica: "Ambientes pausados" },
  { id: "atrasados", Icone: FiAlertTriangle, tom: "danger", singular: "atrasado", plural: "atrasados", dica: "Ambientes na fábrica com o prazo vencido" },
  { id: "tarefasAbertas", Icone: FiCheckSquare, tom: "neutral", singular: "tarefa aberta", plural: "tarefas abertas", dica: "Tarefas aguardando, em andamento ou pausadas" },
  { id: "pecasFora", Icone: FiScissors, tom: "neutral", singular: "peça fora", plural: "peças fora", dica: "Peças desmembradas que ainda não voltaram para o ambiente" },
];

function horaDe(iso) {
  if (!iso) return null;
  const d = new Date(iso);
  if (Number.isNaN(d.getTime())) return null;
  return d.toLocaleTimeString("pt-BR", { hour: "2-digit", minute: "2-digit" });
}

export function Vivo({ atualizadoEm, erro, onRecarregar }) {
  const hora = horaDe(atualizadoEm);
  if (erro) {
    return (
      <StatusVivo data-tom="warning" role="status">
        <span className="ponto" aria-hidden="true" />
        <span>{hora ? `Sem atualizar desde ${hora}` : "Sem conexão com o servidor"}</span>
        <button type="button" onClick={onRecarregar}>Tentar agora</button>
      </StatusVivo>
    );
  }
  return (
    <StatusVivo data-tom="success" role="status" title="O mapa se atualiza sozinho a cada 15 segundos">
      <span className="ponto" aria-hidden="true" />
      {hora ? (
        <span>
          <span className="rotulo-vivo">Ao vivo · </span>
          {hora}
        </span>
      ) : (
        <span>Conectando…</span>
      )}
    </StatusVivo>
  );
}

/**
 * Celular/tablet (≤1024 px): as duas bandejas do cabeçalho ("Ainda no
 * escritório", "Obra futura") entram como os primeiros tiles da faixa — no
 * cabeçalho elas eram duas pílulas grandes empurrando o conteúdo para baixo.
 * O nome acessível mantém o texto do botão do desktop ("Rótulo: N").
 */
function TilesDasBandejas({ bandejas }) {
  const atrasadas = bandejas.semSetorAtrasadas || 0;
  return (
    <>
      <ItemFaixa
        type="button"
        data-tom={atrasadas > 0 ? "danger" : "neutral"}
        aria-label={`${bandejas.rotuloLongo}: ${bandejas.semSetor}${
          atrasadas ? ` · ${plural(atrasadas, "atrasado", "atrasados")}` : ""
        }`}
        title={bandejas.rotuloLongo}
        onClick={() => bandejas.onAbrir(SITUACAO.SEM_SETOR)}
      >
        <strong>{formatarQuantidade(bandejas.semSetor)}</strong>
        <span>{atrasadas ? `${bandejas.rotulo} · ${atrasadas} atras.` : bandejas.rotulo}</span>
      </ItemFaixa>
      <ItemFaixa
        type="button"
        data-tom="neutral"
        aria-label={`Obra futura: ${bandejas.obraFutura}`}
        onClick={() => bandejas.onAbrir(SITUACAO.OBRA_FUTURA)}
      >
        <strong>{formatarQuantidade(bandejas.obraFutura)}</strong>
        <span>obra futura</span>
      </ItemFaixa>
    </>
  );
}

export default function FaixaIndicadores({
  indicadores,
  filtros,
  onAtalho,
  onAbrirSetor,
  vivo,
  compacta = false,
  bandejas = null,
}) {
  const { pessoas, gargalo } = indicadores;
  return (
    <Faixa aria-label="Números da fábrica" data-compacta={compacta ? "sim" : undefined}>
      {bandejas && <TilesDasBandejas bandejas={bandejas} />}
      {ITENS.map(({ id, Icone, tom, singular, plural: rotuloPlural, dica }) => {
        const valor = indicadores[id] || 0;
        const atalho = ATALHOS_DA_FAIXA[id];
        const ligado = atalhoLigado(filtros, atalho);
        return (
          <ItemFaixa
            key={id}
            type="button"
            data-tom={valor > 0 ? tom : "neutral"}
            aria-pressed={ligado}
            title={`${dica}. ${ligado ? "Toque para tirar o filtro." : "Toque para filtrar."}`}
            onClick={() => onAtalho(atalho, ligado)}
          >
            <Icone aria-hidden="true" />
            <strong>{formatarQuantidade(valor)}</strong>
            <span>{valor === 1 ? singular : rotuloPlural}</span>
          </ItemFaixa>
        );
      })}
      <SeparadorFaixa aria-hidden="true" />
      <ItemFaixa as="div" data-tom="neutral" title="Colaboradores ativos no filtro">
        <FiUsers aria-hidden="true" />
        <strong>{formatarQuantidade(pessoas.trabalhando)}</strong>
        <span>trabalhando</span>
        <span className="suave so-largo">
          · {formatarQuantidade(pessoas.pausadas)} em pausa · {plural(pessoas.livres, "livre", "livres")}
        </span>
      </ItemFaixa>
      <SeparadorFaixa aria-hidden="true" />
      {gargalo ? (
        <ItemFaixa
          type="button"
          data-tom="danger"
          title="Setor com mais ambientes atrasados. Toque para abrir o setor."
          onClick={() => onAbrirSetor(gargalo.setorId)}
        >
          <FiTrendingDown aria-hidden="true" />
          <span>Gargalo:</span>
          <strong>{gargalo.nome}</strong>
          <span className="suave so-largo">· {plural(gargalo.atrasadas, "atrasado", "atrasados")}</span>
        </ItemFaixa>
      ) : (
        <ItemFaixa as="div" data-tom="neutral">
          <FiTrendingDown aria-hidden="true" />
          <span>Nenhum setor com atraso</span>
        </ItemFaixa>
      )}
      <span className="empurra" aria-hidden="true" />
      {vivo}
    </Faixa>
  );
}
